import { useEffect, useState } from 'react';
import { EvidensiaOpeningSchedule, isEvidensiaOpeningSchedule } from './evidensia-opening-hours';

const CACHE_DURATION = 24 * 60 * 60 * 1000;
const entries = new Map<string, { updatedAt: number; schedule: EvidensiaOpeningSchedule }>();
const requests = new Map<string, Promise<EvidensiaOpeningSchedule | null>>();
const storageKey = (slug: string) => `evidensia-opening-hours:v1:${slug}`;

function cachedSchedule(slug: string) {
  try {
    const entry =
      entries.get(slug) ?? JSON.parse(window.localStorage.getItem(storageKey(slug)) ?? 'null');
    const age = Date.now() - entry?.updatedAt;
    if (age >= 0 && age < CACHE_DURATION && isEvidensiaOpeningSchedule(entry.schedule)) {
      entries.set(slug, entry);
      return entry.schedule;
    }
  } catch {
    // Blocked or corrupt storage must not prevent loading opening hours.
  }
  return null;
}

function loadSchedule(slug: string) {
  const cached = cachedSchedule(slug);
  if (cached) return Promise.resolve(cached);
  const pending = requests.get(slug);
  if (pending) return pending;
  const promise = (async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(`/api/evidensia-opening-hours?slug=${slug}`, {
        signal: controller.signal,
      });
      if (!response.ok) return null;
      const data = await response.json();
      if (!isEvidensiaOpeningSchedule(data.schedule)) return null;
      const entry = { updatedAt: Date.now(), schedule: data.schedule };
      entries.set(slug, entry);
      try {
        window.localStorage.setItem(storageKey(slug), JSON.stringify(entry));
      } catch {
        // The in-memory result remains available without browser storage.
      }
      return entry.schedule;
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  })().finally(() => requests.delete(slug));
  requests.set(slug, promise);
  return promise;
}

export function useEvidensiaOpeningSchedule(clinicUrl: string, enabled: boolean) {
  const slug =
    clinicUrl.match(/^https:\/\/evidensia\.se\/klinik\/([a-z0-9]+(?:-[a-z0-9]+)*)\/$/)?.[1] ?? null;
  const [state, setState] = useState<{ slug: string; schedule: EvidensiaOpeningSchedule } | null>(
    null,
  );
  useEffect(() => {
    if (!slug || !enabled) return;
    let active = true;
    const cached = cachedSchedule(slug);
    if (cached) setState({ slug, schedule: cached });
    void loadSchedule(slug).then((schedule) => {
      if (active && schedule) setState({ slug, schedule });
    });
    return () => {
      active = false;
    };
  }, [slug, enabled]);
  return state?.slug === slug ? state.schedule : null;
}
